import { useLazyQuery } from '@apollo/client';
import { useCallback } from 'react';
import { CANCELLATION_PREVIEW } from 'graphql/service';

const debug = false;

type CancellationPreviewInput = {
  bookingId?: string;
  groupId?: string;
};

type CancellationPreviewResult = {
  isFree: boolean;
  cancellationFeeAmount: number;
  message: string;
  freeCancellationDeadline: string | null;
  depositAmount: number | null;
};

type CancellationPreviewResponse = {
  cancellationPreview: CancellationPreviewResult;
};

export const useCancellationPreview = () => {
  const [loadCancellationPreview, { data, loading, error, called }] = useLazyQuery<
    CancellationPreviewResponse,
    { data: CancellationPreviewInput }
  >(CANCELLATION_PREVIEW, {
    fetchPolicy: 'no-cache',
  });

  const getCancellationPreview = useCallback(
    async (input: CancellationPreviewInput) => {
      if (!input.bookingId && !input.groupId) {
        if (debug) {
          console.log('useCancellationPreview: bookingId or groupId is required');
        }
        return null;
      }

      try {
        if (debug) {
          console.log('useCancellationPreview request:', input);
        }

        const response = await loadCancellationPreview({
          variables: {
            data: input,
          },
        });

        if (debug) {
          console.log('useCancellationPreview response:', response?.data?.cancellationPreview);
        }

        return response?.data?.cancellationPreview || null;
      } catch (err) {
        if (debug) {
          console.log('useCancellationPreview error:', err);
        }
        return null;
      }
    },
    [loadCancellationPreview]
  );

  return {
    getCancellationPreview,
    cancellationPreview: data?.cancellationPreview || null,
    isFree: data?.cancellationPreview?.isFree ?? false,
    cancellationFeeAmount: data?.cancellationPreview?.cancellationFeeAmount ?? 0,
    message: data?.cancellationPreview?.message || '',
    freeCancellationDeadline: data?.cancellationPreview?.freeCancellationDeadline || null,
    depositAmount: data?.cancellationPreview?.depositAmount ?? null,
    loading,
    error,
    called,
  };
};